import React, { useMemo } from "react";
import { useParams, NavLink } from "react-router-dom";
import { blogPosts } from "../data/blogPosts";

const BlogPost = () => {
  const { slug } = useParams();

  const post = useMemo(() => blogPosts.find((p) => p.slug === slug), [slug]);

  if (!post) {
    return (
      <section className="w-full bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-20">
          <h1 className="text-3xl font-semibold text-[color:var(--navy)]">
            Post not found
          </h1>
          <p className="mt-4 text-[color:var(--muted)]">
            The post you’re looking for doesn’t exist.
          </p>
          <NavLink
            to="/blog"
            className="mt-8 inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold bg-[color:var(--brand-accent)] text-[color:var(--navy)] hover:brightness-95 transition shadow-sm"
          >
            Back to Blog
          </NavLink>
        </div>
      </section>
    );
  }

  return (
    <article className="w-full overflow-x-hidden bg-white">
      <section className="relative w-full overflow-hidden bg-[color:var(--navy)]">
        <div className="absolute inset-0">
          <div className="absolute -top-44 -right-44 h-[32rem] w-[32rem] rounded-full bg-[color:var(--brand-accent)]/22 blur-3xl" />
          <div className="absolute -bottom-52 -left-52 h-[36rem] w-[36rem] rounded-full bg-[color:var(--brand)]/26 blur-3xl" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/25 to-black/50" />
        </div>

        <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-18 sm:py-20">
          <NavLink
            to="/blog"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white/85 hover:text-white transition"
          >
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M19 12H5M5 12L11 6M5 12L11 18"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            Back to Blog
          </NavLink>

          <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white">
            {post.category}
            <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--brand-accent)]" />
            {post.readTime}
          </div>

          <h1 className="mt-6 text-4xl sm:text-5xl font-semibold tracking-tight text-white">
            {post.title}
          </h1>

          <p className="mt-4 text-white/80">
            {post.date}
          </p>
        </div>
      </section>

      <section className="w-full">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="prose max-w-none">
            <p className="text-[color:var(--muted)] leading-relaxed">
              {post.excerpt}
            </p>

            <div className="mt-10 rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] p-8">
              <div className="text-sm font-semibold text-[color:var(--brand)]">
                Placeholder content
              </div>
              <div className="mt-3 text-[color:var(--navy)] font-semibold">
                Next step
              </div>
              <p className="mt-2 text-sm text-[color:var(--muted)] leading-relaxed">
                When you provide your real blog content, we’ll structure it into sections,
                add a table of contents, and keep typography consistent across posts.
              </p>
            </div>
          </div>

          <div className="mt-14 rounded-2xl border border-[color:var(--border)] bg-[color:var(--navy)] p-10">
            <div className="text-2xl font-semibold text-white">
              Want help applying this to your team?
            </div>
            <p className="mt-3 text-white/80">
              Start with a short conversation and we’ll recommend a path forward.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <NavLink
                to="/contact"
                className="inline-flex items-center justify-center rounded-xl px-7 py-3 text-sm font-semibold bg-[color:var(--brand-accent)] text-[color:var(--navy)] hover:brightness-95 transition shadow-lg"
              >
                Talk to Our Team
              </NavLink>
              <NavLink
                to="/services"
                className="inline-flex items-center justify-center rounded-xl px-7 py-3 text-sm font-semibold bg-white/15 text-white hover:bg-white/25 transition"
              >
                View Services
              </NavLink>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
};

export default BlogPost;
